import { DuviNicheSiteButton } from "@/components/duvi-niche-site-button";

export interface DuviNicheSitePricingCardProps {
  /** The tier's admin-edited label (`display_name`) — never its match key. */
  tier: string;
  /** The tier's admin-edited description. */
  valueLine: string;
  price: string;
  period: string;
  features: string[];
  ctaLabel: string;
  /** Registration hand-off, pre-selecting this tier by its immutable `name`. */
  ctaHref: string;
  highlighted: boolean;
  ribbonLabel?: string;
}

/**
 * DuviNicheSitePricingCard — ported from the bespoke Fishwalkers design
 * system's `components/marketing/PricingCard.jsx`
 * (duvi-design/exports/niches/fishwalkers/system.zip).
 *
 * The highlighted card swaps its hairline for a 2px accent border and takes
 * the hover shadow — the only 2px border in the system — and carries the
 * ribbon. Highlighting is read from the tier document, never derived from a
 * card's position in the grid.
 *
 * `tier`, `valueLine`, `features` and `ctaHref` all carry real data from
 * `/tiers.json` plus this repo's own bullet copy; the export's placeholder
 * tiers and prices are visual language only and are not reproduced.
 */
export function DuviNicheSitePricingCard({
  tier,
  valueLine,
  price,
  period,
  features,
  ctaLabel,
  ctaHref,
  highlighted,
  ribbonLabel = "Most popular",
}: DuviNicheSitePricingCardProps) {
  return (
    <article
      className={[
        "relative flex flex-col gap-[var(--space-5)] rounded-[var(--radius-card)] bg-[var(--surface-card)] p-[var(--space-8)]",
        highlighted
          ? "border-2 border-[var(--accent)] shadow-[var(--shadow-card-hover)]"
          : "border border-[var(--border)] shadow-[var(--shadow-card)]",
      ].join(" ")}
    >
      {highlighted && (
        <span className="absolute -top-[13px] left-[var(--space-8)] inline-flex items-center rounded-[var(--radius-badge)] bg-[var(--accent)] px-[10px] py-[5px] font-[family-name:var(--font-body)] text-[13px] font-semibold leading-none text-[color:var(--accent-foreground)]">
          {ribbonLabel}
        </span>
      )}
      <div className="flex flex-col gap-[var(--space-2)]">
        <h3 className="font-[family-name:var(--font-display)] text-[length:var(--text-h3)] font-bold leading-[var(--leading-heading)] text-[color:var(--heading)]">
          {tier}
        </h3>
        <p className="m-0 font-[family-name:var(--font-body)] text-[15px] leading-[var(--leading-lead)] text-[color:var(--muted)]">
          {valueLine}
        </p>
      </div>
      <p className="m-0 flex items-baseline gap-[6px]">
        <span className="font-[family-name:var(--font-display)] text-[40px] font-extrabold leading-none tracking-[var(--tracking-display)] text-[color:var(--heading)]">
          {price}
        </span>
        <span className="font-[family-name:var(--font-body)] text-[15px] text-[color:var(--muted)]">
          {period}
        </span>
      </p>
      <ul className="m-0 flex list-none flex-col gap-[var(--space-3)] p-0">
        {features.map((feature) => (
          <li
            key={feature}
            className="flex items-start gap-[var(--space-3)] font-[family-name:var(--font-body)] text-[15px] leading-[1.5] text-[color:var(--body)]"
          >
            <span
              aria-hidden="true"
              className="mt-[7px] h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--brand)]"
            />
            {feature}
          </li>
        ))}
      </ul>
      <DuviNicheSiteButton
        href={ctaHref}
        fullWidth
        variant={highlighted ? "primary" : "secondary"}
        className="mt-auto"
      >
        {ctaLabel}
      </DuviNicheSiteButton>
    </article>
  );
}
